import React, { Component, RefObject } from 'react';
import { InputAdornment, Menu, TextField, Button } from '@material-ui/core';
import { Clear, Search } from '@material-ui/icons';
import * as Styled from 'components/TimeKeepersList/styled';
import AppStore from 'store/app.store';
import { inject } from 'mobx-react';
import { RootStore } from 'store/root.store';
import { Platform } from '../../util/Platform';
import { CollaborateType, TimeKeeperAssignment } from '../../api/types/types';
import { withTranslation } from 'react-i18next';

interface Props {
    render: (tkMenuAnchor: RefObject<HTMLElement | HTMLButtonElement>, 
             openTkList: (
                 event: React.MouseEvent<HTMLButtonElement | HTMLLIElement | HTMLElement> |  React.KeyboardEvent<HTMLElement>
             ) => void) => JSX.Element,
    collaborate: boolean;
    menuWidth?: number;
    appStore?: AppStore;
    timeKeepers: TimeKeeperAssignment[];
    workDate: string;
    matterId?: number | null;
    selectedTimeKeepers?: CollaborateType[];
    saveCollaboratees?: (tks: CollaborateType[]) => void;
    // tslint:disable-next-line:no-any
    t: any;
}
interface State {
    tkMenuOpen: boolean;
    selectedTks: CollaborateType[];
    searchText: string;
    enableSave: boolean;
}
@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        appStore: rootStore.appStore
    };
})
class TimeKeepersList extends Component<Props, State> {
    timeKeeperMenuAnchor = React.createRef<HTMLDivElement>();
    isFetching = false; // used to make one api call at a time
    state: State = {
        tkMenuOpen: false,
        selectedTks: [],
        searchText: '',
        enableSave: false
    };
    searchRef = React.createRef<HTMLInputElement>();
    saveRef = React.createRef<HTMLElement>();
    cancelRef = React.createRef<HTMLElement>();
    componentWillMount(): void {
        this.setState({ selectedTks: this.props.selectedTimeKeepers || [] });
    }
    componentWillUnmount(): void {
        this.setState({ selectedTks: [] });
    }
    componentDidUpdate(prevProps: Readonly<Props>) {
        // handles when cancelling changes to entry that has collaborators added but not saved
        const selectedTks = this.props.selectedTimeKeepers || [];
        const prevSelectedTks = prevProps.selectedTimeKeepers || [];
        if (prevSelectedTks.length !== selectedTks.length && selectedTks.length === 0) {
            this.setState({ selectedTks: [] });
        }
    }
    fetchMoreTimeKeepers = async (cHeight: number, scrollHeight: number, scrollTop: number, workDate: string, matterId?: number | null) => {
        if (this.props.collaborate) {
            let bottom = scrollHeight - (scrollTop + cHeight);
            if (scrollTop === 0) {
                return;
            }
            if (bottom <= 15 && !this.isFetching) {
                this.isFetching = true;
                await this.props.appStore!.fetchMoreTimeKeepers(workDate, matterId);
                this.isFetching = false;
            }
        }
    }
    openTkList = (event: React.MouseEvent<HTMLButtonElement | HTMLLIElement | HTMLElement>) => {
        this.setState({ tkMenuOpen: true });
        this.props.appStore!.setTkSearchText('');
        this.setState({ searchText: '' });
        if (this.searchRef.current) {
            this.searchRef.current.focus();
        }
    }
    handleTkSearch = (evt: React.ChangeEvent<HTMLInputElement>) => {
        let search = evt.target.value || '';
        this.setState({ searchText: search });
        this.props.appStore!.setTkSearchText(search);
        if (this.props.collaborate) {
            this.props.appStore!.debouncedGetTimekeepersList(this.props.workDate, this.props.matterId);
        }
    }
    handleTkSelection = (val: TimeKeeperAssignment) => {
        let tkList = Array.from(this.state.selectedTks);
        const index = tkList.findIndex(tk => tk.timeKeeperId === val.timeKeeperId);
        
        // if the item exist in the array then remove it, otherwise push to array
        if (index > -1) {
            tkList.splice(index, 1);
        } else {
            tkList.push({
                timeKeeperId: val.timeKeeperId,
                tkName: val.name
            });
        }

        // previous and current TK list to be compared to identify if there are any change to enable save button
        const newListSorted = JSON.stringify(tkList.sort((a, b) => a.timeKeeperId - b.timeKeeperId));
        const oldListSorted = JSON.stringify((this.props.selectedTimeKeepers || []).sort((a, b) => a.timeKeeperId - b.timeKeeperId));

        this.setState({
            selectedTks: tkList,
            enableSave: newListSorted !== oldListSorted
        });
    }
    handleTabKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, collaborate: boolean) => {
        if (collaborate && e.key === 'Tab' && !e.shiftKey && !e.ctrlKey) {
            e.preventDefault();
            e.stopPropagation();
            let el = this.saveRef.current!;
            if (el.tabIndex === -1) { // when Save button is disabled shift focus to Cancel
                el = this.cancelRef.current!;
            }
            el.focus();
        }
        if (e.key === 'Escape' && !e.shiftKey && !e.ctrlKey) {
            e.nativeEvent.stopImmediatePropagation();
            if (!collaborate) {
                this.onMenuClose();
            }
        }
    }
    saveCollabs = () => {
        if (this.props.saveCollaboratees) {
            this.props.saveCollaboratees(this.state.selectedTks);
        }
        this.closeDialog();
    }
    isTkSelected = (id: number) => {
        return this.state.selectedTks.findIndex(tk => tk.timeKeeperId === id) > -1;
    }
    setTimeKeeper = async (tkId: number) => {
        const { appStore, t } = this.props;
        if (appStore!.online) {
            await appStore!.setTimekeeper(tkId);
            if (Platform.isElectron() && appStore!.rootStore.api.TimeEntry.getServerEntries) {
                appStore!.rootStore.api.TimeEntry.getServerEntries!(tkId);
            }
        } else {
            appStore!.rootStore.snackbarStore.triggerSnackbar(t('timekeeper.list.snackbar.offline_change'));
        }
    }
    onMenuClose = () => {
        this.setState({ selectedTks: this.props.selectedTimeKeepers || [] });
        this.closeDialog();
        setTimeout(() => {
            this.props.appStore!.currentEnd = 0;
            this.props.appStore!.allTimeKeepersList = [];
        }, 500);
    }
    closeDialog = () => {
        this.setState({ enableSave: false, tkMenuOpen: false }, () => {
            this.props.appStore!.focusPreviousActiveElement();
        });
    }
    render() {
        const { tkMenuOpen, searchText } = this.state;
        const { collaborate, menuWidth, timeKeepers, appStore, t } = this.props;
        return (
            <>
                {this.props.render(this.timeKeeperMenuAnchor, this.openTkList)}
                    <Menu
                        anchorOrigin={{ horizontal: 'center', vertical: 'center' }}
                        anchorEl={this.timeKeeperMenuAnchor.current}
                        open={tkMenuOpen}
                        onClose={!collaborate ? this.onMenuClose : undefined}
                        onKeyDown={(e) => this.handleTabKeyDown(e, collaborate)}
                        PaperProps={{
                            style: {
                                maxHeight: 48 * 8,
                                minWidth: menuWidth,
                                overflowY: 'auto'
                            }
                        }}
                        MenuListProps={{ disablePadding: true }}
                        onEntered={() => {
                            if (this.searchRef.current) {
                                this.searchRef.current.focus();
                            }
                        }}
                        disableRestoreFocus={collaborate}
                        disableBackdropClick={collaborate}
                        disableEscapeKeyDown={collaborate}
                    >
                        <Styled.SearchHeaderItem
                            onKeyDown={(e) => {
                                if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Tab') {
                                    this.searchRef.current!.focus();
                                }
                            }}
                        >
                            <TextField
                                id={'search-with-icon-textfield'}
                                placeholder={t('timekeeper.list.field.search.placeholder')}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position={'start'}>
                                            <Search />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchText ? (
                                        <InputAdornment
                                            position={'end'}
                                            onClick={() => this.handleTkSearch({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>)}
                                        >
                                            <Clear style={{ width: 20, cursor: 'pointer' }} />
                                        </InputAdornment>
                                    ) : null
                                }}
                                value={searchText}
                                fullWidth={true}
                                onChange={this.handleTkSearch}
                                style={{
                                    padding: 5,
                                    outline: 'none'
                                }}
                                inputRef={this.searchRef}
                            />
                        </Styled.SearchHeaderItem>
                        {timeKeepers.length > 0 ?
                            timeKeepers.map((timeKeeper, index) => {
                                // created a variable to avoid build errors
                                let isSelected = timeKeeper.timeKeeperId === appStore!.currentTimekeeper.timeKeeperId;
                                return (
                                    <Styled.TKMenuItem
                                        selected={isSelected}
                                        onClick={isSelected ? undefined : collaborate ?
                                            () => this.handleTkSelection(timeKeeper)
                                            :
                                            () => this.setTimeKeeper(timeKeepers[index].timeKeeperId)
                                        }
                                        key={index}
                                    >
                                        <Styled.NameSpan>
                                            {`${timeKeeper.name} (${timeKeeper.office})`}
                                        </Styled.NameSpan>
                                        {(!isSelected && collaborate) &&
                                            <div style={{ margin: '10px' }}>
                                                <Styled.CollaborateCheckbox
                                                    checked={this.isTkSelected(timeKeeper.timeKeeperId)}
                                                    onClick={() => this.handleTkSelection(timeKeeper)}
                                                />
                                            </div>
                                        }
                                    </Styled.TKMenuItem>
                                );
                            })
                        :
                            <div style={{ paddingLeft: 12 }}>{t('timekeeper.list.empty')}</div>
                        }
                        {collaborate &&
                            <>
                                <hr style={{ marginBottom: '0px' }} />
                                <Styled.ActionButton>
                                    <Button
                                        buttonRef={this.saveRef}
                                        onClick={this.saveCollabs}
                                        disabled={!this.state.enableSave}
                                    >
                                        {t('save')}
                                    </Button>
                                    <Button buttonRef={this.cancelRef} onClick={this.onMenuClose}>{t('cancel')}</Button>
                                </Styled.ActionButton>
                            </>
                        }
                    </Menu>
            </>
        );
    }
}

export default withTranslation(['common'])(TimeKeepersList);